import { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';

import { ConfigurablePosesScannerView } from 'inquiry/scanners/ConfigurablePosesScanner/ConfigurablePosesScannerView';
import {
  PoseState,
  PoseStateManager,
} from 'inquiry/scanners/ConfigurablePosesScanner/PoseStateManager';
import { usePoseDetectionModel } from 'inquiry/scanners/ConfigurablePosesScanner/usePoseDetectionModel';
import { useVideoCapture } from 'inquiry/scanners/ConfigurablePosesScanner/useVideoCapture';
import { useCameraInspection } from 'lib/hooks/useCameraInspection';
import useIsMounted from 'lib/hooks/useIsMounted';
import { SerializedStepSelfieStyles, StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { generateWebGLDebugInfo, isIOS } from 'lib/utils/device';
import { SelfieVideoCaptureMethod } from 'verify/lib/interfaces/CaptureOption';
import {
  CapturedSelfie,
  CapturedSelfiesSubmitData,
  SelfieStep as SelfiePose,
} from 'verify/lib/interfaces/selfie';
import { BLANK_CAPTURE_BYTE_SIZE } from 'verify/lib/utils/webcam';
import { CaptureMethod } from 'verify/models/verification';

interface ConfigurablePosesScannerProps {
  assets?: StepSelfieConfig['assets']['record-page'];
  delayUntilManualCaptureMs?: number | null;
  detectTimeout?: number; // The amount of time we should allow before sending them to stillfie
  enableVideoRecording?: boolean;
  localizations?: StepSelfieConfig['localizations']['capture-page'];
  uploadProgress?: number;
  poseOrder: SelfiePose[];
  requireHigherAccuracyCapture: boolean;
  styles?: SerializedStepSelfieStyles;
  videoCaptureMethods?: SelfieVideoCaptureMethod[];
  videoConstraints?: MediaTrackConstraints;
  videoSessionJwt?: string | null;
  enableMobilePendingExperienceForWeb: boolean;
  handleComplete: (capturedSelfiesSubmitData: CapturedSelfiesSubmitData) => void;
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleRestartStep?: (handleRestartStepArgs?: { timeExceeded?: boolean }) => void;
  handleSlowFaceDetection: () => void;
}

const DETECT_INTERVAL = 250;
const VIDEO_ELEMENT_HEIGHT = 250;
const DEFAULT_PROFILE_SIZE = 250;
const IMAGE_QUALITY = 0.92;

export const ConfigurablePosesScanner = ({
  assets,
  delayUntilManualCaptureMs,
  detectTimeout,
  enableVideoRecording,
  localizations,
  poseOrder,
  requireHigherAccuracyCapture,
  styles,
  videoCaptureMethods,
  videoConstraints,
  videoSessionJwt,
  handleComplete,
  handleFailed,
  handleRestartStep,
  handleSlowFaceDetection,
  enableMobilePendingExperienceForWeb,
}: ConfigurablePosesScannerProps) => {
  /**
   * NB: All of these values need to be refs because they are referenced
   * in the tick timeout - if they are set up as state variables then it will get
   * stale
   */
  const webcamRef = useRef<Webcam | null>(null);
  const detectionTimeoutRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(new Date().getTime());
  const capturedPosesRef = useRef<CapturedSelfie[]>([]);
  const poseStateManagerRef = useRef<PoseStateManager | null>(null);

  const tickCountRef = useRef<number>(0);
  const tickCountDelayRef = useRef<number>(30);

  const isMounted = useIsMounted();

  const [hasSentData, setHasSentData] = useState<boolean>(false);
  const [poseState, setPoseState] = useState<PoseState | null>(null);
  const [videoWidthOverride, setVideoWidthOverride] = useState<number | null>(null);
  const [videoHeightOverride, setVideoHeightOverride] = useState<number | null>(null);

  const { getCanvasFromVideo, canRunPoseDetection, getPoseDetectionData } = usePoseDetectionModel({
    handleFailed,
    handleModelLoaded: async () => {
      await runTick();
    },
  });
  const { logCameraInspectionFromStream, logCameraInspectionSnapshot, getCameraInspectionData } =
    useCameraInspection();
  const [profileSize, setProfileSize] = useState<number>(DEFAULT_PROFILE_SIZE);

  const captureIsDone =
    !!poseState &&
    poseState.currentPose === 'done' &&
    capturedPosesRef.current.length === poseOrder.length;

  const {
    capturedVideoData,
    isAudioEnabled,
    isVideoReady,
    initVideoCapture,
    stopVideoCapture,
    cleanUpVideoCapture,
  } = useVideoCapture({
    enableVideoRecording: !!enableVideoRecording,
    videoCaptureMethods: videoCaptureMethods || ['stream'],
    videoSessionJwt,
    captureIsDone,
    handleFailed,
    handleRestartStep,
  });

  const resizeVideo = () => {
    if (webcamRef.current?.video == null) {
      return;
    }
    tickCountDelayRef.current = tickCountRef.current;
    const { clientWidth, videoHeight, videoWidth } = webcamRef.current.video;
    if (videoHeight > videoWidth) {
      // If the video is portrait, force it to be square aspect ratio
      setVideoHeightOverride(clientWidth);
    } else {
      // If the video is landscape, set the width to be large enough so the height is at least 250px (VIDEO_ELEMENT_HEIGHT)
      const videoAspectRatio = videoWidth / videoHeight;
      const newVideoWidth = Math.floor(
        Math.max(clientWidth, VIDEO_ELEMENT_HEIGHT * videoAspectRatio),
      );
      setVideoWidthOverride(newVideoWidth);
    }
  };

  // Runs the "tick" for autocapture. A tick is run every DETECT_INTERVAL.
  // However, because the next tick is not scheduled until processing is
  // finished, DETECT_INTERVAL represents the MINIMUM time between ticks. There
  // should never be more than 1 tick being evaluated at once.
  //
  // WARNING: Because this function is defined inline and used in a setTimeout,
  // state values accessed in this function will always be stale. Use refs
  // instead.
  const runTick = async () => {
    const tickStartTime = new Date().getTime();

    let nextPoseState;
    try {
      // we wait 5 ticks (this is a minimum of 0.5 seconds, but can be slower based on requestAnimationFrame)
      // before calling FaceAPI detection to let the feed reach a normal state first to avoid weirdness
      // such as extremely tiny feed. through testing on an iphone 10 XR, this number can go as low as 1
      // without bugging out with the tiny feed, which may suggest that only the very first frame is problematic,
      // but a few more frames were added to be more generous in case other devices take longer to let the feed
      // get normal
      if (!isIOS() || tickCountRef.current > tickCountDelayRef.current + 5) {
        nextPoseState = await updatePoseState();
      }
    } catch (e) {
      captureException(e, webcamRef.current ? 'warning' : 'info', {
        ...generateWebGLDebugInfo(),
        ...getSentryErrorContext(),
      });
      // If the webcam is still mounted, then the error is not due to unmounting,
      // so we should route to a failed page
      if (webcamRef.current) {
        handleFailed(e as unknown as string | Error, 'capture');
      }
      return;
    }

    const processingTime = new Date().getTime() - tickStartTime;

    if (tickCountRef.current === 0 && detectTimeout != null && processingTime > detectTimeout) {
      // NOTE: Device incompatibility means this can be >10 seconds (1k over 3 months).
      // In that case, we fallback to stillfie. It's too hard to when this will happen.
      handleSlowFaceDetection();
      return;
    }

    // If state is done, then just exit and let the useEffect handle sending up the data
    if (nextPoseState?.currentPose === 'done') {
      stopVideoCapture();
      return;
    }

    tickCountRef.current++;

    // If component doesn't exist, don't enqueue another runTick
    if (!isMounted) {
      return;
    }

    // Set up next timeout to run pose detection again
    const nextFrameDelay = Math.max(0, DETECT_INTERVAL - processingTime);
    detectionTimeoutRef.current = window.setTimeout(() => {
      if (window.requestAnimationFrame != null) {
        // setTimeout effectively sets a "floor" for rAF. This will tick
        // every 100ms at fastest, and will slow down via rAF if needed.
        window.requestAnimationFrame(runTick);
      } else {
        runTick();
      }
    }, nextFrameDelay);
  };

  const updatePoseState = async () => {
    // https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
    if (poseStateManagerRef.current == null) {
      poseStateManagerRef.current = new PoseStateManager({
        poseOrder,
        requireHigherAccuracyCapture,
        onCapture: (pose: SelfiePose, canvas: HTMLCanvasElement, isManual: boolean) => {
          // If video doesn't exist, then the component unmounted
          if (webcamRef.current?.video == null) {
            return;
          }

          const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
          const captureMethod = isManual ? CaptureMethod.Manual : CaptureMethod.Auto;

          if (dataUrl == null || dataUrl.length < BLANK_CAPTURE_BYTE_SIZE) {
            captureMessage(
              `Selfie ${captureMethod} capture captured a potentially blank image`,
              'warning',
              getSentryErrorContext(),
            );
            handleFailed(
              `Selfie ${captureMethod} capture captured a potentially blank image`,
              'capture',
            );
            return;
          }

          capturedPosesRef.current = [
            ...capturedPosesRef.current,
            {
              capturedAt: new Date().toISOString(),
              captureMethod,
              dataUrl,
              pose,
            },
          ];
        },
      });
    }

    const webcam = webcamRef.current;

    if (!webcam?.video || !canRunPoseDetection(webcam?.video) || !isVideoReady()) {
      return;
    }

    const poseData = await getPoseDetectionData(webcam.video);

    // If poseData is null that means something got unmounted (video or canvas)
    if (poseData == null || !webcam?.video) {
      return;
    }

    const nextPoseState = poseStateManagerRef.current.getNextPoseState({
      canvas: poseData.canvas,
      forceCapture: false,
      poseData: {
        elapsedTime: Date.now() - startTimeRef.current,
        faceBox: poseData.faceBox,
        faceCount: poseData.faceCount,
        faceDimensions: poseData.faceDimensions,
        horizontalAngle: poseData.horizontalAngle,
        landmarks: poseData.landmarks,
        videoDimensions: { width: webcam.video.videoWidth, height: webcam.video.videoHeight },
      },
    });

    setPoseState(nextPoseState);
    setProfileSize(Math.min(webcam.video.clientWidth, webcam.video.clientHeight) - 8);

    if (poseData.faceBox != null) {
      logCameraInspectionSnapshot(poseData.canvas);
    }

    return nextPoseState;
  };

  const handleManualCapture = () => {
    const pose = poseState?.currentPose;

    if (
      pose === 'done' ||
      pose == null ||
      webcamRef.current?.video == null ||
      poseStateManagerRef.current == null
    ) {
      return;
    }

    const canvas = getCanvasFromVideo(webcamRef.current.video);
    if (canvas == null) {
      captureMessage(
        `Selfie manual capture has a blank canvas`,
        'warning',
        getSentryErrorContext(),
      );
      return;
    }

    setPoseState(
      poseStateManagerRef.current.getNextPoseState({
        canvas,
        forceCapture: true,
        poseData: null,
      }),
    );
  };

  const getSentryErrorContext = () => ({
    tickCount: tickCountRef.current,
    elapsedTime: Date.now() - startTimeRef.current,
  });

  useEffect(() => {
    // Clean up when the component unmounts
    return () => {
      if (detectionTimeoutRef.current != null) {
        clearTimeout(detectionTimeoutRef.current);
        detectionTimeoutRef.current = null;
      }

      cleanUpVideoCapture();
    };
  }, []);

  useEffect(() => {
    if (
      !hasSentData &&
      captureIsDone &&
      ((enableVideoRecording && capturedVideoData) || !enableVideoRecording)
    ) {
      const startTimeISO = startTimeRef.current
        ? new Date(startTimeRef.current).toISOString()
        : undefined;
      handleComplete({
        capturedSelfies: capturedPosesRef.current,
        captureStartedAt: startTimeISO,
        cameraInspectionData: getCameraInspectionData({ includeSmallImages: true }),
        capturedVideoData,
      });
      setHasSentData(true);
    }
  }, [
    captureIsDone,
    capturedVideoData,
    enableVideoRecording,
    hasSentData,
    getCameraInspectionData,
    handleComplete,
  ]);

  return (
    <ConfigurablePosesScannerView
      assets={assets}
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      localizations={localizations}
      poseOrder={poseOrder}
      poseState={poseState}
      profileSize={profileSize}
      styles={styles}
      showLoadingSpinner={!isVideoReady()}
      videoHeight={videoHeightOverride}
      handleManualCapture={handleManualCapture}
      enableMobilePendingExperienceForWeb={enableMobilePendingExperienceForWeb}
    >
      <Webcam
        audio={isAudioEnabled()}
        height='initial'
        onUserMedia={async (stream) => {
          await logCameraInspectionFromStream(stream);
          initVideoCapture(stream, webcamRef.current?.video);
        }}
        onUserMediaError={(error) => handleFailed(error, 'load')}
        ref={(ref: Webcam & HTMLVideoElement) => {
          webcamRef.current = ref;

          if (ref?.video) {
            ref.video.addEventListener('canplay', resizeVideo);

            // There is a longstanding React bug where the muted attribute is not
            // propagated to the DOM <video> element. This can cause permissions
            // related issues.
            // Sample fix: https://stackoverflow.com/a/66153842
            // Github issue: https://github.com/facebook/react/issues/6544#issuecomment-705702546
            // Remove if upstream fix merged: https://github.com/mozmorris/react-webcam/issues/332
            ref.video.defaultMuted = true;
            ref.video.muted = true;
          }
        }}
        videoConstraints={videoConstraints}
        width={videoWidthOverride ?? '100%'}
      />
    </ConfigurablePosesScannerView>
  );
};
